import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Profile } from "@/hooks/useProfiles";

const profileSchema = z.object({
  name: z.string().min(1, "Name is required"),
  video_codec: z.string().nullable().optional(),
  video_width: z.coerce.number().int().positive().nullable().optional(),
  video_height: z.coerce.number().int().positive().nullable().optional(),
  video_bitrate: z.coerce.number().int().positive().nullable().optional(),
  video_framerate: z.string().nullable().optional(),
  audio_codec: z.string().nullable().optional(),
  audio_bitrate: z.coerce.number().int().positive().nullable().optional(),
  audio_sample_rate: z.coerce.number().int().positive().nullable().optional(),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

interface ProfileFormProps {
  profile?: Profile;
  onSubmit: (data: ProfileFormValues) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const ProfileForm = ({
  profile,
  onSubmit,
  onCancel,
  isLoading,
}: ProfileFormProps) => {
  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: profile?.name ?? "1080p",
      video_codec: profile?.video_codec ?? "h264",
      video_width: profile?.video_width ?? 1920,
      video_height: profile?.video_height ?? 1080,
      video_bitrate: profile?.video_bitrate ?? 10000,
      video_framerate: profile?.video_framerate ?? "50",
      audio_codec: profile?.audio_codec ?? "aac",
      audio_bitrate: profile?.audio_bitrate ?? 128,
      audio_sample_rate: profile?.audio_sample_rate ?? 48000,
    },
  });

  const handleSubmit = (values: ProfileFormValues) => {
    // Convert empty strings/zeros to null
    const cleaned = {
      ...values,
      video_width: values.video_width || null,
      video_height: values.video_height || null,
      video_bitrate: values.video_bitrate || null,
      audio_bitrate: values.audio_bitrate || null,
      audio_sample_rate: values.audio_sample_rate || null,
    };
    onSubmit(cleaned);
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          placeholder="1080p30 8Mbps"
          {...form.register("name")}
        />
        {form.formState.errors.name && (
          <p className="text-sm text-destructive">
            {form.formState.errors.name.message}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Video Codec</Label>
          <Select
            value={form.watch("video_codec") ?? "h264"}
            onValueChange={(v) => form.setValue("video_codec", v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="h264">H.264</SelectItem>
              <SelectItem value="h265">H.265/HEVC</SelectItem>
              <SelectItem value="av1">AV1</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Framerate</Label>
          <Select
            value={form.watch("video_framerate") ?? "30"}
            onValueChange={(v) => form.setValue("video_framerate", v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24">24 fps</SelectItem>
              <SelectItem value="25">25 fps</SelectItem>
              <SelectItem value="29.97">29.97 fps</SelectItem>
              <SelectItem value="30">30 fps</SelectItem>
              <SelectItem value="50">50 fps</SelectItem>
              <SelectItem value="59.94">59.94 fps</SelectItem>
              <SelectItem value="60">60 fps</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="video_width">Width</Label>
          <Input
            id="video_width"
            type="number"
            placeholder="1920"
            {...form.register("video_width")}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="video_height">Height</Label>
          <Input
            id="video_height"
            type="number"
            placeholder="1080"
            {...form.register("video_height")}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="video_bitrate">Video Bitrate (kbps)</Label>
        <Input
          id="video_bitrate"
          type="number"
          placeholder="8000"
          {...form.register("video_bitrate")}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Audio Codec</Label>
          <Select
            value={form.watch("audio_codec") ?? "aac"}
            onValueChange={(v) => form.setValue("audio_codec", v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="aac">AAC</SelectItem>
              <SelectItem value="opus">Opus</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="audio_bitrate">Audio Bitrate (kbps)</Label>
          <Input
            id="audio_bitrate"
            type="number"
            placeholder="192"
            {...form.register("audio_bitrate")}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="audio_sample_rate">Sample Rate (Hz)</Label>
        <Input
          id="audio_sample_rate"
          type="number"
          placeholder="48000"
          {...form.register("audio_sample_rate")}
        />
      </div>

      <div className="flex gap-2 pt-2">
        <Button type="submit" disabled={isLoading}>
          {profile ? "Update" : "Create"} Profile
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};
